import {Button} from 'antd';
import {useEffect} from 'react';

import {Locale} from '../../../provider/locale/locale';
import {LangKeyType} from '../../../provider/locale/translation/type';
import {useSystem} from '../../../provider/system/system-hook';
import {AnalyticsTarget, track} from '../../../service/analytics/analytics';
import {classNames} from '../../../util/css';
import {useFormat} from '../../../util/format-hook/format-hook';

import * as styles from './table-control-panel.scss';

type PropsType = {
    selectedCount: number;
    totalCount: number;

    selectedCountLabel: LangKeyType;

    toggleAllButtonConfig: {
        langKey: LangKeyType;
        onClick: VoidFunction;
        isLoading?: boolean;
    };

    submitButtonConfig: {
        langKey: LangKeyType;
        onClick?: VoidFunction;
        isLoading: boolean;
    };

    secondaryButtonConfig?: {
        langKey: LangKeyType;
        onClick: VoidFunction;
        isLoading: boolean;
        isDashed?: boolean;
    };

    thirdButtonConfig?: {
        langKey: LangKeyType;
        onClick: VoidFunction;
        isLoading: boolean;
    };

    fourthButtonConfig?: {
        langKey: LangKeyType;
        onClick: VoidFunction;
        isLoading: boolean;
    };

    onCancel?: VoidFunction;
    onDelete?: () => void;

    isVisible: boolean;

    selectedCountContainerClassname?: string;

    disableSelectedCount?: boolean;
};

// eslint-disable-next-line complexity
export function TableControlPanel(props: PropsType): JSX.Element | null {
    const {
        selectedCountLabel,
        selectedCount,
        totalCount,
        toggleAllButtonConfig,
        onCancel,
        onDelete,
        isVisible,
        submitButtonConfig,
        secondaryButtonConfig,
        thirdButtonConfig,
        fourthButtonConfig,
        selectedCountContainerClassname,
        disableSelectedCount,
    } = props;

    const {
        langKey: toggleButtonLangKey,
        onClick: handleToggleAll,
        isLoading: isToggleAllLoading,
    } = toggleAllButtonConfig;
    const {langKey: submitButtonLangKey, onClick: onSubmit, isLoading: isMainActionInProgress} = submitButtonConfig;

    const isLoading = Boolean(
        isMainActionInProgress ||
            secondaryButtonConfig?.isLoading ||
            thirdButtonConfig?.isLoading ||
            fourthButtonConfig?.isLoading
    );

    const {getFormattedNumber} = useFormat();
    const {screen} = useSystem();

    useEffect(() => {
        if ((screen.isDesktop || screen.isTablet || screen.isMobile) && isVisible) {
            document.body.dataset.tableControlPanelOpen = 'true';
        } else {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        }

        return () => {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        };
    }, [isVisible, screen.isDesktop, screen.isTablet, screen.isMobile]);

    useEffect(() => {
        const floatingButton = document.querySelector('#hde-container');

        if (selectedCount > 0) {
            floatingButton?.setAttribute('hidden', 'true');
        }

        return () => {
            floatingButton?.removeAttribute('hidden');
        };
    }, [selectedCount]);

    function handleSubmit() {
        track(AnalyticsTarget.ReviewsGenerator.NotificationCampaigns.SelectClients);
        onSubmit?.();
    }

    if (!isVisible) {
        return null;
    }

    return (
        <div className={styles.TableControlPanel}>
            <div className={classNames(styles.TableControlPanel_selected, selectedCountContainerClassname)}>
                <div className={styles.TableControlPanel_count}>
                    {!disableSelectedCount ? (
                        <>
                            <Locale stringKey={selectedCountLabel} />
                            <span className={styles.TableControlPanel_countValue}>
                                <strong>{getFormattedNumber(selectedCount)}</strong> /{' '}
                                <span className={styles.TableControlPanel_countTotal}>
                                    {getFormattedNumber(totalCount)}
                                </span>
                            </span>
                        </>
                    ) : (
                        <Locale
                            stringKey="REVIEWS__SELECTED_COUNT"
                            valueMap={{
                                selectedCount: String(selectedCount),
                            }}
                        />
                    )}
                </div>
                <Button
                    disabled={isToggleAllLoading}
                    loading={isToggleAllLoading}
                    onClick={handleToggleAll}
                    size="small"
                >
                    <Locale stringKey={toggleButtonLangKey} />
                </Button>
            </div>

            <div className={styles.TableControlPanel_buttons}>
                {onCancel && (
                    <Button className={styles.TableControlPanel_cancelButton} onClick={onCancel}>
                        <Locale stringKey="BUTTON__CANCEL" />
                    </Button>
                )}
                {onDelete && (
                    <Button danger disabled={selectedCount === 0} onClick={onDelete}>
                        <Locale stringKey="REVIEWS_GENERATOR__CLIENTS__ACTIONS__REMOVE" />
                    </Button>
                )}
                {secondaryButtonConfig && (
                    <Button
                        disabled={!selectedCount || isLoading}
                        loading={secondaryButtonConfig.isLoading}
                        onClick={() => secondaryButtonConfig.onClick()}
                        type={secondaryButtonConfig.isDashed === false ? 'default' : 'dashed'}
                    >
                        <Locale stringKey={secondaryButtonConfig.langKey} />
                    </Button>
                )}
                {thirdButtonConfig && (
                    <Button
                        disabled={!selectedCount || isLoading}
                        loading={thirdButtonConfig.isLoading}
                        onClick={() => thirdButtonConfig.onClick()}
                    >
                        <Locale stringKey={thirdButtonConfig.langKey} />
                    </Button>
                )}
                {fourthButtonConfig && (
                    <Button
                        disabled={!selectedCount || isLoading}
                        loading={fourthButtonConfig.isLoading}
                        onClick={() => fourthButtonConfig.onClick()}
                    >
                        <Locale stringKey={fourthButtonConfig.langKey} />
                    </Button>
                )}
                {onSubmit && (
                    <Button
                        className={styles.TableControlPanel_submitButton}
                        disabled={!selectedCount || isLoading}
                        loading={isMainActionInProgress}
                        onClick={handleSubmit}
                        type="primary"
                    >
                        <Locale stringKey={submitButtonLangKey} />
                    </Button>
                )}
            </div>
        </div>
    );
}
